// =============================================================================
// FOCUS SERVICE - Firebase CRUD for GoalFocus
// =============================================================================
//
// GoalFocus beheert de actieve kern-intentie en focus-categorie per doel.
// Er kan maar 1 actieve focus per doel zijn.
//
// Collection: /goalFocus/{focusId}
//
// =============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { GoalFocus } from '../types';

const COLLECTION_NAME = 'goalFocus';

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Create a new focus for a goal
 * Automatically deactivates any previous focus for the same goal
 * 
 * @param ownerId - User ID
 * @param focusData - Focus data (without id)
 * @returns Focus ID
 */
export async function createGoalFocus(
  ownerId: string,
  focusData: Omit<GoalFocus, 'id'>
): Promise<string> {
  // Generate new ID
  const focusRef = doc(collection(db, COLLECTION_NAME));
  const focusId = focusRef.id;

  // Create focus document
  const focus: GoalFocus = {
    id: focusId,
    ...focusData,
    ownerId, // Ensure ownerId is set
  };

  await setDoc(focusRef, focus);

  return focusId;
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get focus by ID
 */
export async function getFocusById(focusId: string): Promise<GoalFocus | null> {
  const focusRef = doc(db, COLLECTION_NAME, focusId);
  const focusSnap = await getDoc(focusRef);

  if (!focusSnap.exists()) {
    return null;
  }

  return focusSnap.data() as GoalFocus;
}

/**
 * Get the active focus for a goal
 * Returns the most recent focus (highest detectedAt)
 * 
 * @param ownerId - User ID
 * @param goalId - Goal ID
 * @returns GoalFocus or null if no focus exists
 */
export async function getActiveFocusForGoal(
  ownerId: string,
  goalId: string
): Promise<GoalFocus | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    orderBy('detectedAt', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].data() as GoalFocus;
}

/**
 * Get all focus history for a goal
 * Ordered by detectedAt (most recent first)
 */
export async function getFocusHistoryForGoal(
  ownerId: string,
  goalId: string
): Promise<GoalFocus[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    orderBy('detectedAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => doc.data() as GoalFocus);
}

/**
 * Get all focuses for a user (across all goals)
 */
export async function getAllFocuses(ownerId: string): Promise<GoalFocus[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    orderBy('detectedAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => doc.data() as GoalFocus);
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Update a focus (e.g., mark as confirmed by user)
 */
export async function updateGoalFocus(
  focusId: string,
  updates: Partial<GoalFocus>
): Promise<void> {
  const focusRef = doc(db, COLLECTION_NAME, focusId);
  
  // Remove id from updates if present
  const { id, ...updateData } = updates as any;
  
  await setDoc(focusRef, updateData, { merge: true });
}

/**
 * Mark a focus as confirmed by user
 */
export async function confirmFocus(focusId: string): Promise<void> {
  await updateGoalFocus(focusId, {
    confirmedByUser: true,
  });
}

// -----------------------------------------------------------------------------
// DELETE
// -----------------------------------------------------------------------------

/**
 * Delete a focus
 */
export async function deleteGoalFocus(focusId: string): Promise<void> {
  const focusRef = doc(db, COLLECTION_NAME, focusId);
  await deleteDoc(focusRef);
}

/**
 * Delete all focuses for a goal
 * Called when a goal is deleted
 */
export async function deleteFocusesForGoal(
  ownerId: string,
  goalId: string
): Promise<void> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  
  const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
}
